import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AIQueryBox = ({ onQuery, reportType }) => {
    const [query, setQuery] = useState('');
    const [isQuerying, setIsQuerying] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const audioChunks = useRef([]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!query.trim()) {
            toast.error('Please enter a query');
            return;
        }

        setIsQuerying(true);
        try {
            await onQuery(query);
            setQuery('');
        } catch (error) {
            toast.error('Failed to process query');
        } finally {
            setIsQuerying(false);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 16000
            });

            audioChunks.current = [];

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunks.current.push(event.data);
                }
            };

            recorder.onstop = async () => {
                const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm;codecs=opus' });
                await transcribeAudio(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };

            recorder.start();
            setMediaRecorder(recorder);
            setIsRecording(true);
            toast.success('Recording started...');
        } catch (error) {
            toast.error('Failed to access microphone');
            console.error('Microphone error:', error);
        }
    };

    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
            setIsRecording(false);
            toast.info('Processing audio...');
        }
    };

    const transcribeAudio = async (audioBlob) => {
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'audio.webm');

            const backendUrl = 'https://pyapi.shipez.ai' || '';
            const response = await fetch(`${backendUrl}/api/voice/transcribe`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Transcription failed');
            }

            const data = await response.json();
            if (data.text) {
                setQuery(data.text);
                toast.success('Voice query captured!');
            }
        } catch (error) {
            toast.error('Failed to transcribe audio');
            console.error('Transcription error:', error);
        }
    };

    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
        >
            <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                </div>
                <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">AI Query Assistant</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Ask questions about your {reportType} data</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                    <textarea
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="e.g., 'Show me top 5 customers by revenue last month' or 'What's the average booking value this quarter?'"
                        className="w-full px-4 py-3 pr-24 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 resize-none"
                        rows={3}
                        disabled={isQuerying || isRecording}
                    />
                    <div className="absolute bottom-3 right-3 flex items-center space-x-2">
                        <button
                            type="button"
                            onClick={isRecording ? stopRecording : startRecording}
                            className={`p-2 rounded-lg transition-all ${isRecording
                                ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                                : 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300'
                                }`}
                            disabled={isQuerying}
                        >
                            {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                        </button>
                        <button
                            type="submit"
                            disabled={isQuerying || !query.trim() || isRecording}
                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {isQuerying ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span>Processing...</span>
                                </>
                            ) : (
                                <>
                                    <Send className="w-5 h-5" />
                                    <span>Submit</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>

            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Pro Tip:</strong> Use natural language to query your data. Try asking for trends, comparisons, or specific metrics!
                </p>
            </div>
        </motion.div>
    );
};

export default AIQueryBox;